import { useAdOperatorAvailability } from '@backoffice-frontend/ad-operator-availability'
import {
  useErrorHandlingMutation,
  useErrorHandlingSubscription,
} from '@backoffice-frontend/data-fetching'
import { BackofficeMetricUnit } from '@backoffice-frontend/graphql'
import { useMoiaErrorNotification } from '@backoffice-frontend/patterns'
import { useGlobalRegion } from '@backoffice-frontend/service-area-picker'
import { useMetricTracking } from '@backoffice-frontend/tracking'
import { useBackofficeLoggedInUser } from '@backoffice-frontend/user-management-ui'
import type { Dispatch, ReactNode, SetStateAction } from 'react'
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useFccMostCriticalVehicleSubscription } from './AdVehicleStream.subscription.hook'
import { useStartHandlingVehicleMutation } from './StartHandlingVehicle.hook'

const ORPHAN_TIMEOUT_MS = 30_000
const METRICS_NAMESPACE = 'FleetControl/ad-vehicle-stream'

type StartHandlingOptions = {
  vehicleId: string
  isTakeOver?: boolean
  onCompleted?: () => void
}

type AdVehicleStreamValue = {
  vehicleId?: string
  fleetId?: string
  handledByBackofficeUserId?: string
  isHandledByCurrentUser: boolean
  pendingUpdate: boolean
  setPendingUpdate: Dispatch<SetStateAction<boolean>>
  autoOpenChat: boolean
  setAutoOpenChat: Dispatch<SetStateAction<boolean>>
  startHandling: (options: StartHandlingOptions) => Promise<boolean>
  isStartingHandling: boolean
  recordStreamLoaded: () => void
}

export const AdVehicleStreamContext = createContext<
  AdVehicleStreamValue | undefined
>(undefined)

type AdVehicleStreamProviderProps = {
  children: ReactNode
}

export const AdVehicleStreamProvider = ({
  children,
}: AdVehicleStreamProviderProps) => {
  const { t } = useTranslation()
  const { user } = useBackofficeLoggedInUser()
  const [region] = useGlobalRegion()
  const { isActive: shouldSubscribeToMostCriticalVehicle } =
    useAdOperatorAvailability()
  const { enqueueMoiaErrorNotification } = useMoiaErrorNotification()
  const { trackMetric } = useMetricTracking()

  const [vehicleId, setVehicleId] = useState<string | undefined>(undefined)
  const [fleetId, setFleetId] = useState<string | undefined>(undefined)
  const [pendingUpdate, setPendingUpdate] = useState<boolean>(false)
  const [autoOpenChat, setAutoOpenChat] = useState(false)

  const takeoverClickedAtRef = useRef<number | undefined>(undefined)
  const orphanTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  )

  const [mutate, { loading: isStartingHandling }] = useErrorHandlingMutation(
    useStartHandlingVehicleMutation,
  )

  const [handledByBackofficeUserId, setHandledByBackofficeUserId] = useState<
    string | undefined
  >(undefined)

  const isHandledByCurrentUser = Boolean(
    !!vehicleId &&
      handledByBackofficeUserId &&
      user?.id &&
      handledByBackofficeUserId === user.id,
  )

  const clearOrphanTimeout = useCallback(() => {
    if (orphanTimeoutRef.current) {
      clearTimeout(orphanTimeoutRef.current)
      orphanTimeoutRef.current = undefined
    }
  }, [])

  const startHandling = useCallback(
    async ({
      vehicleId: targetVehicleId,
      isTakeOver,
      onCompleted,
    }: StartHandlingOptions): Promise<boolean> => {
      takeoverClickedAtRef.current = Date.now()
      setPendingUpdate(true)

      clearOrphanTimeout()
      orphanTimeoutRef.current = setTimeout(() => {
        void trackMetric({
          metricName: 'TakeoverOrphanedClick',
          value: 1,
          unit: BackofficeMetricUnit.Count,
          dimensions: [{ name: 'vehicleId', value: targetVehicleId }],
          namespace: METRICS_NAMESPACE,
        })
        takeoverClickedAtRef.current = undefined
      }, ORPHAN_TIMEOUT_MS)

      const result = await mutate({
        variables: { vehicleId: targetVehicleId, isTakeOver },
        onCompleted: () => onCompleted?.(),
        onError: () => {
          enqueueMoiaErrorNotification(
            t(
              'fleetMap_notification_alertCardsCommonVehicleAlreadyHandled_label',
            ),
          )
          setPendingUpdate(false)
          clearOrphanTimeout()
          takeoverClickedAtRef.current = undefined
        },
      })

      return !result?.errors
    },
    [mutate, trackMetric, enqueueMoiaErrorNotification, t, clearOrphanTimeout],
  )

  const recordStreamLoaded = useCallback(() => {
    if (!takeoverClickedAtRef.current) return

    clearOrphanTimeout()

    const durationMs = Date.now() - takeoverClickedAtRef.current
    const dimensions = [
      ...(vehicleId ? [{ name: 'vehicleId', value: vehicleId }] : []),
      ...(fleetId ? [{ name: 'fleetId', value: fleetId }] : []),
    ]

    void trackMetric({
      metricName: 'TakeoverToStreamOpenDurationMs',
      value: durationMs,
      unit: BackofficeMetricUnit.Milliseconds,
      dimensions,
      namespace: METRICS_NAMESPACE,
    })

    takeoverClickedAtRef.current = undefined
  }, [vehicleId, fleetId, trackMetric, clearOrphanTimeout])

  const prevIsHandledByCurrentUserRef = useRef(isHandledByCurrentUser)
  useEffect(() => {
    if (prevIsHandledByCurrentUserRef.current !== isHandledByCurrentUser) {
      console.info('[AdVehicleStream] isHandledByCurrentUser changed', {
        isHandledByCurrentUser,
        vehicleId,
        handledByBackofficeUserId,
        userId: user?.id,
      })
      prevIsHandledByCurrentUserRef.current = isHandledByCurrentUser
    }
  }, [isHandledByCurrentUser, vehicleId, handledByBackofficeUserId, user?.id])

  const isSubscriptionSkipped =
    !user?.id || !region || !shouldSubscribeToMostCriticalVehicle
  useEffect(() => {
    if (isSubscriptionSkipped) {
      console.info('[AdVehicleStream] subscription skipped', {
        hasUserId: !!user?.id,
        hasRegion: !!region,
        shouldSubscribeToMostCriticalVehicle,
      })
    }
  }, [
    isSubscriptionSkipped,
    user?.id,
    region,
    shouldSubscribeToMostCriticalVehicle,
  ])

  useErrorHandlingSubscription(
    useFccMostCriticalVehicleSubscription,
    // Only if user is available or busy we want to subscribe to vehicle handling updates:
    user?.id && region && shouldSubscribeToMostCriticalVehicle
      ? {
          variables: {
            backofficeUserId: user.id,
            regionId: region.id,
          },
          fetchPolicy: 'no-cache',
          onError: (e: unknown) => {
            console.error('Error in most critical vehicle subscription', e)
            setVehicleId(undefined)
            setPendingUpdate(false)
          },
          onData: ({ data }) => {
            if (data.data?.fccMostCriticalVehicle) {
              const receivedVehicleId =
                data.data.fccMostCriticalVehicle.vehicle?.id ?? undefined
              const receivedHandledBy =
                data.data.fccMostCriticalVehicle.vehicle
                  ?.handledByBackofficeUserId ?? undefined
              const receivedFleetId =
                data.data.fccMostCriticalVehicle.fleetId ?? undefined

              console.info('[AdVehicleStream] subscription onData', {
                vehicleId: receivedVehicleId,
                handledByBackofficeUserId: receivedHandledBy,
              })

              setVehicleId(receivedVehicleId)
              setFleetId(receivedFleetId)
              setHandledByBackofficeUserId(receivedHandledBy)
              setPendingUpdate(false)
            }
          },
        }
      : { skip: true },
  )

  const value = useMemo(
    () => ({
      vehicleId,
      fleetId,
      handledByBackofficeUserId,
      isHandledByCurrentUser,
      pendingUpdate,
      setPendingUpdate,
      autoOpenChat,
      setAutoOpenChat,
      startHandling,
      isStartingHandling,
      recordStreamLoaded,
    }),
    [
      vehicleId,
      fleetId,
      handledByBackofficeUserId,
      isHandledByCurrentUser,
      pendingUpdate,
      autoOpenChat,
      startHandling,
      isStartingHandling,
      recordStreamLoaded,
    ],
  )

  return (
    <AdVehicleStreamContext.Provider value={value}>
      {children}
    </AdVehicleStreamContext.Provider>
  )
}
